import { ShotStatus } from "../types/home";
import type { Shot } from "../app/services/shots";

/**
 * ショット一覧テーブル（スケジュールビュー）用の 5 ラベルステータス。
 *
 * バックエンドの 7 値ステータス体系は変更せず、表示・編集時に以下へマッピングする:
 * - 準備中:       テイク未納品（メディアアイテム未登録）かつ status 1/2/4
 * - チェックバック: status 1/2/4（テイク納品済み・クライアント確認待ち）
 * - 制作中:       status 3（制作側作業中）
 * - 保留:         status 7
 * - 完了:         status 5/6
 */
export type ShotTableStatusKey =
  | "preparing"
  | "checkback"
  | "in_production"
  | "on_hold"
  | "completed";

export const SHOT_TABLE_STATUS_LABELS: Record<ShotTableStatusKey, string> = {
  preparing: "準備中",
  checkback: "チェックバック",
  in_production: "制作中",
  on_hold: "保留",
  completed: "完了",
};

/** ソート時の表示順（準備中 → チェックバック → 制作中 → 保留 → 完了） */
export const SHOT_TABLE_STATUS_ORDER: ShotTableStatusKey[] = [
  "preparing",
  "checkback",
  "in_production",
  "on_hold",
  "completed",
];

/** 5 ラベルキー → PATCH で送る代表ステータス値 */
export const TABLE_STATUS_TO_SHOT_STATUS: Record<ShotTableStatusKey, number> = {
  preparing: ShotStatus.CLIENT_PENDING, // 4（テイク未納品なら表示は準備中のまま）
  checkback: ShotStatus.CLIENT_PENDING, // 4
  in_production: ShotStatus.PRODUCER_PENDING, // 3
  on_hold: ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED, // 7
  completed: ShotStatus.APPROVED, // 5
};

/** ショットにテイク（メディアアイテム）が納品済みかどうか */
export const shotHasMedia = (shot: Shot): boolean =>
  !!(shot.media_item_updated_at || shot.media_file || shot.thumbnail);

/** ステータス値 + メディア有無 → 5 ラベルキー */
export const getTableStatusKey = (
  status: number,
  hasMedia: boolean,
): ShotTableStatusKey => {
  switch (status) {
    case ShotStatus.PRODUCER_PENDING:
      return "in_production";
    case ShotStatus.PRODUCER_DONE_CLIENT_UNAPPROVED:
      return "on_hold";
    case ShotStatus.APPROVED:
    case ShotStatus.APPROVED_WITH_DELIVERY_FILE:
      return "completed";
    // NEW / UPDATE / CLIENT_PENDING
    default:
      return hasMedia ? "checkback" : "preparing";
  }
};

export const getShotTableStatusKey = (shot: Shot): ShotTableStatusKey =>
  getTableStatusKey(shot.status, shotHasMedia(shot));

/** 次の納品日を編集できるステータスか（準備中・制作中のみ編集可） */
export const isDeliveryDateEditable = (key: ShotTableStatusKey): boolean =>
  key === "preparing" || key === "in_production";

/**
 * ステータスセルの選択肢（プロデューサー側 = 全ステータス）。
 * 準備中とチェックバックはどちらも status 4 に対応するため、
 * メディア有無に応じて表示ラベルが一致する方のみを出す。
 */
export const getTableStatusOptions = (
  hasMedia: boolean,
): { value: ShotTableStatusKey; name: string }[] =>
  (hasMedia
    ? (["checkback", "in_production", "on_hold", "completed"] as const)
    : (["preparing", "in_production", "on_hold", "completed"] as const)
  ).map((key) => ({ value: key, name: SHOT_TABLE_STATUS_LABELS[key] }));

/**
 * クライアント側で許可するステータス遷移（現在値 → 遷移先の集合）。
 * - チェックバック・保留 → 完了
 * - 保留・完了 → チェックバック
 * - 準備中・制作中は変更不可（空配列 = 選択肢を出さない）
 */
const CLIENT_STATUS_TRANSITIONS: Partial<
  Record<ShotTableStatusKey, ShotTableStatusKey[]>
> = {
  checkback: ["checkback", "completed"],
  on_hold: ["on_hold", "checkback", "completed"],
  completed: ["completed", "checkback"],
};

/** クライアント側のステータスセル選択肢。変更不可のステータスでは空配列を返す */
export const getClientTableStatusOptions = (
  currentKey: ShotTableStatusKey,
): { value: ShotTableStatusKey; name: string }[] =>
  (CLIENT_STATUS_TRANSITIONS[currentKey] ?? []).map((key) => ({
    value: key,
    name: SHOT_TABLE_STATUS_LABELS[key],
  }));

/** 貼り付けテキスト → 5 ラベルキー（一致しない場合は null） */
export const parseTableStatusLabel = (
  text: string,
): ShotTableStatusKey | null => {
  const normalized = text.trim();
  if (!normalized) return null;
  const entry = (
    Object.entries(SHOT_TABLE_STATUS_LABELS) as [ShotTableStatusKey, string][]
  ).find(([, label]) => label === normalized);
  return entry ? entry[0] : null;
};
